import { CellClassParams, ColDef, ValueSetterParams } from "ag-grid-community";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { ModalComponent } from "../..";
import { useFileUpload } from "../../../../customHooks/useFileUpload";
import { useModalControl } from "../../../../customHooks/useModalControl";
import {
  deepCopy,
  formatDate,
  formatToCurrency,
  formatToNumber,
  getOffsetFromPage,
  getPageFromOffset,
} from "../../../../helperFunctions";
import { useGetCooperativeMembersQuery } from "../../../../redux/features/membership/membership.api";
import { CooperativeMembersQuery } from "../../../../redux/features/membership/membership.type";
import {
  ManualPaymentMethods,
  ManualPaymentMethodsMap,
} from "../../../../utils/constants";
import { ManualPaymentMethod } from "../../../../utils/types";
import { Button } from "../../../buttons/button";
import { Icon } from "../../../icons/icon";
import { SearchInput } from "../../../inputs/searchInput";
import Pagination from "../../../pagination/pagination";
import { BulkEntryFileNameModal } from "../bulkEntryFileNameModal";
import { FileUploadModal } from "../fileUpload";
import { ImageViewModal } from "../imageViewModal";

type DataItem = {
  memberId: number;
  memberName: string;
  memberEmail: string;
  amount: number;
  paymentDate?: Date;
  paymentMethod?: string;
  paymentProof?: string;
};

const isDataValid = (val: DataItem, type: "debit" | "upload") => {
  return (
    (val.amount ?? 0) > 0 &&
    val.memberEmail !== undefined &&
    val.memberId !== undefined &&
    val.memberName !== undefined &&
    val.paymentDate !== undefined &&
    (type === "upload"
      ? val.paymentMethod !== undefined && val.paymentProof !== undefined
      : true)
  );
};

const FileInputRenderer = (props: CustomCellRendererProps) => {
  const {
    modal,
    modalOpen: isModalOpen,
    handleModalClose,
    handleModalOpen,
  } = useModalControl();

  const { uploading, handleSingleFileUpload } = useFileUpload();

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            handleModalOpen("viewFileModal");
          }}
          disabled={!props.value}
          className={`${
            props.value ? "text-pry-green-200 font-medium" : ""
          } flex items-center gap-2`}
        >
          {props?.value ? (
            <Icon svg={"small-document-icon"} width={16} height={16} />
          ) : undefined}
          {props?.value ? "View" : "No upload"}
        </button>

        <button
          type="button"
          className="p-1 rounded-full bg-[#D1D9D7] text-pry-dark-green-300"
          onClick={() => {
            handleModalOpen("uploadFileModal");
          }}
        >
          <Icon svg={props?.value ? "edit" : "attach"} width={16} height={16} />
        </button>
      </div>

      <FileUploadModal
        modalOpen={isModalOpen && modal === "uploadFileModal"}
        handleModalClose={() => {
          handleModalClose();
        }}
        onSuccess={({ file, reset }) => {
          handleSingleFileUpload(file, (url) => {
            props.setValue?.(url);
            reset();
          });
        }}
        loading={uploading}
      />

      {props.value ? (
        <ImageViewModal
          modalOpen={isModalOpen && modal === "viewFileModal"}
          onModalClose={() => {
            handleModalClose();
          }}
          src={props.value}
        />
      ) : undefined}
    </>
  );
};

export const BulkEntryModal = ({
  modalOpen,
  onModalClose,
  onSuccess,
  type = "upload",
  title = "Bulk Entry",
  loading,
  amountType = "currency",
}: {
  modalOpen: boolean;
  onModalClose: () => void;
  onSuccess: (data: {
    data: DataItem[];
    name: string;
    close: () => void;
    reset: () => void;
  }) => void;
  type?: "upload" | "debit";
  title?: string;
  loading?: boolean;
  amountType?: "currency" | "units";
}) => {
  const [query, setQuery] = useState<CooperativeMembersQuery>({
    offset: 0,
    limit: 10000,
    coop: 43,
    text: undefined,
  });
  const [rows, setRows] = useState<DataItem[]>([]);
  const [columns, setColumns] = useState<ColDef<DataItem>[]>([]);

  const dataRef = useRef<DataItem[]>([]);

  const {
    modal,
    modalOpen: isModalOpen,
    handleModalClose,
    handleModalOpen,
  } = useModalControl();

  const { data: members, isFetching: fetchingMembers } =
    useGetCooperativeMembersQuery(query, {
      refetchOnMountOrArgChange: true,
    });

  useEffect(() => {
    if (members?.data?.results) {
      setColumns([
        {
          field: "memberId",
          headerName: "ID",
          editable: false,
          hide: true,
          flex: type === "debit" ? 1 : undefined,
        },
        {
          field: "memberName",
          headerName: "Name",
          editable: false,
          flex: type === "debit" ? 1 : undefined,
        },
        {
          field: "memberEmail",
          headerName: "Email",
          editable: false,
          flex: type === "debit" ? 1 : undefined,
        },
        {
          field: "amount",
          headerName: amountType === "currency" ? "Amount" : "Units",
          editable: true,
          valueFormatter: (v) => {
            return amountType === "currency"
              ? formatToCurrency(v.value as number, {
                  currency: "NGN",
                  decimalPlaces: 2,
                })
              : formatToNumber(v.value, { decimalPlaces: 0 });
          },
          cellEditor: "agNumberCellEditor",
          cellEditorParams: {
            min: 0,
            // max: 100,
          },
          valueSetter: (params: ValueSetterParams<DataItem>) => {
            const data = params.data;
            data.amount = params.newValue;
            return true;
          },
          flex: type === "debit" ? 1 : undefined,
        },
        {
          field: "paymentDate",
          headerName: "Payment Date",
          editable: true,
          cellEditor: "agDateCellEditor",
          cellEditorParams: {
            max: formatDate(new Date(), "YYYY-MM-DD"),
            includeTime: true,
          },
          valueFormatter: (v) =>
            v.value
              ? formatDate(v.value as Date, "MMM DD, YYYY hh:mm A")
              : "Select date & time",
          valueSetter: (params: ValueSetterParams<DataItem>) => {
            const data = params.data;
            data.paymentDate = params.newValue;
            return true;
          },
          hide: type === "debit",
          flex: type === "debit" ? 1 : undefined,
        },
        {
          field: "paymentMethod",
          headerName: "Payment Method",
          editable: true,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: ManualPaymentMethods,
          },
          hide: type === "debit",
          flex: type === "debit" ? 1 : undefined,
          valueFormatter: (v) => {
            return v.value
              ? ManualPaymentMethodsMap[v.value as ManualPaymentMethod]
              : "Select payment method";
          },
        },
        {
          field: "paymentProof",
          headerName: "Payment Proof",
          editable: false,
          cellRenderer: FileInputRenderer,
          hide: type === "debit",
          flex: type === "debit" ? 1 : undefined,
        },
      ]);

      setRows(
        members.data.results.map((member) => {
          const existing = dataRef.current.find(
            (d) => d.memberId === member.id
          );

          return (
            existing ?? {
              memberId: member.id,
              memberName: `${member.User.FirstName} ${member.User.LastName}`,
              memberEmail: `${member.User.Email}`,
              amount: 0,
              paymentDate: undefined,
              paymentMethod: undefined,
              paymentProof: undefined,
            }
          );
        })
      );
    }
  }, [members, type, amountType]);

  return (
    <>
      <ModalComponent
        isModalOpen={modalOpen}
        onModalClose={() => {
          onModalClose();
        }}
        topTitle
        title={title}
        subTitle=""
        modalClassName="md:!max-w-[95%] !h-screen !max-h-[90dvh]"
        childrenClassName={"flex flex-col"}
        hasButtons={false}
        modalStyle="center"
        showMobileTopBar={false}
      >
        <>
          <div className="flex flex-col gap-4 flex-1">
            <div className="items-center flex flex-col md:flex-row gap-4 justify-between">
              <SearchInput
                placeholder="Search member name"
                searchFunction={(val) => {
                  setQuery({ ...query, text: val ?? "", offset: 0 });
                }}
                className="w-full md:max-w-[286px] !px-4 !py-2"
                iconDimension={16}
              />

              <p
                className="ml-auto max-md:order-first flex items-center text-sm font-medium text-info-purple gap-2"
                id={"bulk-upload-tooltip"}
                data-tooltip-id="tooltip"
              >
                Guide
                <Icon svg="help-circle" width={16} height={16} />
              </p>

              <Tooltip id="tooltip" className="tooltip" clickable>
                <ul className="list-disc list-outside pl-3">
                  <li>Double click on a cell to edit.</li>
                  <li>
                    Ensure all fields are filled for the intended members.
                  </li>
                  <li>Entries with amout as zero (0) will be ignored.</li>
                </ul>
              </Tooltip>
            </div>

            <div className="w-full flex-1 overflow-x-scroll min-h-[200px]">
              <AgGridReact
                gridOptions={{
                  rowHeight: 46,
                  rowStyle: {
                    maxHeight: "42px",
                  },
                }}
                rowData={rows}
                columnDefs={columns}
                defaultColDef={{
                  headerClass: "bulk-table-header-cell",
                  cellClass: (params: CellClassParams<DataItem>) => {
                    return classNames(
                      "bulk-table-row-cell",
                      {
                        "!bg-light-red-bg":
                          params?.data?.amount && !params?.value,
                        "!bg-light-grey !cursor-not-allowed !border-0":
                          !params?.colDef?.editable &&
                          params.colDef.field !== "paymentProof",
                        "!text-pry-dark-green-400/50 !font-normal":
                          !params?.value,
                      },
                      params.colDef.cellClass
                    );
                  },
                }}
                loading={fetchingMembers}
                onPasteStart={() => {}}
                onPasteEnd={() => {}}
                onCellValueChanged={(params) => {
                  setRows((prev) => {
                    return prev.map((row) =>
                      row.memberId === params.data.memberId ? params.data : row
                    );
                  });

                  const dataCopy = deepCopy(dataRef.current);

                  if (
                    dataCopy
                      .map((d) => d.memberId)
                      .includes(params.data.memberId)
                  ) {
                    dataRef.current = dataCopy.map((row) =>
                      row.memberId === params.data.memberId ? params.data : row
                    );
                  } else {
                    dataCopy.push(params.data);
                    dataRef.current = dataCopy;
                  }
                }}
              />
            </div>

            <Pagination
              currentPage={getPageFromOffset(
                query.offset ?? 0,
                query.limit ?? 0
              )}
              totalCount={members?.data?.count ?? 0}
              itemsPerPage={query?.limit ?? 0}
              onPageChange={(page) => {
                setQuery({
                  ...query,
                  offset: getOffsetFromPage(page, query.limit ?? 0),
                });
              }}
              pageRangeDisplayed={4}
              showPagNumbers={false}
            />

            <div className="items-center grid grid-cols-2 md:flex md:flex-row gap-4 justify-end">
              <Button
                type="button"
                text={"Clear Entries"}
                onClick={() => {
                  dataRef.current = [];

                  setRows((prev) => {
                    return prev.map((d) => {
                      return {
                        ...d,
                        amount: 0,
                        paymentDate: undefined,
                        paymentMethod: undefined,
                        paymentProof: undefined,
                      };
                    });
                  });
                }}
                size="small"
                className="md:!max-w-fit"
                variation="secondary-dark"
              />

              <Button
                type="button"
                text={"Submit"}
                onClick={() => {
                  if (type === "upload") {
                    handleModalOpen("nameEntryModal");
                  }
                }}
                size="small"
                className="md:!max-w-fit"
                variation="primary"
                disabled={
                  dataRef.current.filter((val) => isDataValid(val, type))
                    .length !==
                    dataRef.current.filter((val) => (val.amount ?? 0) > 0)
                      .length ||
                  dataRef.current.filter((val) => isDataValid(val, type))
                    .length === 0
                }
              />
            </div>
          </div>
        </>
      </ModalComponent>

      <BulkEntryFileNameModal
        modalOpen={isModalOpen && modal === "nameEntryModal"}
        handleModalClose={() => {
          if (!loading) {
            handleModalClose();
          }
        }}
        onSuccess={({ name, reset }) => {
          const validData = dataRef.current.filter((val) =>
            isDataValid(val, type)
          );

          onSuccess({
            data: validData,
            name: name,
            close: () => {
              handleModalClose();
            },
            reset: () => {
              reset();

              dataRef.current = [];

              setRows((prev) => {
                return prev.map((d) => {
                  return {
                    ...d,
                    amount: 0,
                    paymentDate: undefined,
                    paymentMethod: undefined,
                    paymentProof: undefined,
                  };
                });
              });
            },
          });
        }}
        loading={loading}
      />
    </>
  );
};
